import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  CheckSquare,
  Trash2,
  Clock,
  Copy,
  FileText,
  ChevronDown,
  ChevronRight,
  MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function TasksPage() {
  const { user } = useContext(AuthContext);
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  
  // Form states
  const [newChecklist, setNewChecklist] = useState({
    title: "",
    description: "",
    project_id: "",
    visibility: "everyone",
    is_template: false,
    items: []
  });
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemEstimate, setNewItemEstimate] = useState("");

  const isOwner = user?.role === 'owner';
  const isManager = isOwner || user?.role === 'project_manager' || user?.role === 'office_admin';
  const canEditDelete = isManager; // All managers see buttons, backend enforces owner-only delete via approval

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [checklistsRes, templatesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/checklists?is_template=false`).catch(() => ({ data: [] })),
        axios.get(`${API}/checklists?is_template=true`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] }))
      ]);
      setChecklists(checklistsRes.data || []);
      setTemplates(templatesRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChecklist = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/checklists`, newChecklist);
      if (newChecklist.is_template) {
        setTemplates([response.data, ...templates]);
      } else {
        setChecklists([response.data, ...checklists]);
      }
      setShowCreateModal(false);
      resetForm();
      toast.success("Checklist created!");
    } catch (error) {
      toast.error("Failed to create checklist");
    }
  };

  const handleDeleteChecklist = async (checklistId, isTemplate) => {
    if (!window.confirm("Are you sure you want to delete this checklist?")) return;
    try {
      const res = await axios.delete(`${API}/checklists/${checklistId}`);
      if (res.data?.detail?.includes("approval")) {
        toast.info("Deletion request sent to owner for approval");
        return;
      }
      if (isTemplate) {
        setTemplates(templates.filter(t => t.checklist_id !== checklistId));
      } else {
        setChecklists(checklists.filter(c => c.checklist_id !== checklistId));
      }
      setShowDetailModal(null);
      toast.success("Checklist moved to trash");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to delete";
      if (msg.includes("approval")) toast.info(msg);
      else toast.error(msg);
      toast.error("Failed to delete checklist");
    }
  };

  const handleDuplicateTemplate = async (templateId, projectId = null) => {
    try {
      const response = await axios.post(`${API}/checklists/${templateId}/duplicate`, {
        project_id: projectId
      });
      setChecklists([response.data, ...checklists]);
      toast.success("Checklist created from template!");
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  const handleToggleItem = async (checklist, itemId) => {
    const item = (checklist.items || []).find(i => i.item_id === itemId);
    if (!item) return;
    
    try {
      const response = await axios.put(`${API}/checklists/${checklist.checklist_id}/items/${itemId}`, {
        is_completed: !item.is_completed
      });
      
      const updated = response.data;
      // Ensure response has items (full checklist) before updating state
      if (updated && updated.items) {
        const total = updated.items.length;
        const completed = updated.items.filter(i => i.is_completed).length;
        updated.completion = { total, completed, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
        
        setChecklists(checklists.map(c => c.checklist_id === checklist.checklist_id ? updated : c));
        if (showDetailModal?.checklist_id === checklist.checklist_id) {
          setShowDetailModal(updated);
        }
      }
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleAddItem = async (checklistId) => {
    if (!newItemTitle.trim()) return;
    
    try {
      const response = await axios.post(`${API}/checklists/${checklistId}/items`, {
        title: newItemTitle,
        time_estimate_minutes: newItemEstimate ? parseInt(newItemEstimate) : null
      });
      
      const newItem = response.data;
      
      // Append new item to the checklist's items array
      const updatedChecklists = checklists.map(c => {
        if (c.checklist_id !== checklistId) return c;
        const updatedItems = [...(c.items || []), newItem];
        const total = updatedItems.length;
        const completed = updatedItems.filter(i => i.is_completed).length;
        return {
          ...c,
          items: updatedItems,
          completion: { total, completed, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 }
        };
      });
      setChecklists(updatedChecklists);
      
      // Update the detail modal if open
      if (showDetailModal?.checklist_id === checklistId) {
        const updated = updatedChecklists.find(c => c.checklist_id === checklistId);
        if (updated) setShowDetailModal(updated);
      }
      
      setNewItemTitle("");
      setNewItemEstimate("");
      setShowAddItemModal(false);
      toast.success("Item added");
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  const handleDeleteItem = async (checklistId, itemId) => {
    try {
      await axios.delete(`${API}/checklists/${checklistId}/items/${itemId}`);
      
      const checklist = checklists.find(c => c.checklist_id === checklistId);
      if (checklist) {
        const updatedChecklist = {
          ...checklist,
          items: checklist.items.filter(i => i.item_id !== itemId)
        };
        setChecklists(checklists.map(c => 
          c.checklist_id === checklistId ? updatedChecklist : c
        ));
        if (showDetailModal?.checklist_id === checklistId) {
          setShowDetailModal(updatedChecklist);
        }
      }
      toast.success("Item deleted");
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const addItemToForm = () => {
    if (!newItemTitle.trim()) return;
    setNewChecklist({
      ...newChecklist,
      items: [...newChecklist.items, { 
        title: newItemTitle, 
        time_estimate_minutes: newItemEstimate ? parseInt(newItemEstimate) : null 
      }]
    });
    setNewItemTitle("");
    setNewItemEstimate("");
  };

  const removeItemFromForm = (index) => {
    setNewChecklist({
      ...newChecklist,
      items: newChecklist.items.filter((_, i) => i !== index)
    });
  };

  const resetForm = () => {
    setNewChecklist({
      title: "",
      description: "",
      project_id: "",
      is_template: false,
      items: []
    });
    setNewItemTitle("");
    setNewItemEstimate("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="checklists-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="checklists-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Checklists
          </h1>
          <p className="text-slate-600 mt-1">
            {checklists.length} active checklists • {templates.length} templates
          </p>
        </div>
        {isManager && (
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-checklist-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Checklist
          </Button>
        )}
      </div>

      {/* Templates Section */}
      {templates.length > 0 && isManager && (
        <Card data-testid="templates-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Templates
            </CardTitle>
            <CardDescription>Reusable checklist templates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <div 
                  key={template.checklist_id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{template.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {template.items.length} items
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDuplicateTemplate(template.checklist_id)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Use Template
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteChecklist(template.checklist_id, true)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Checklists */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="checklists-grid">
        {checklists.map(checklist => (
          <ChecklistCard 
            key={checklist.checklist_id}
            checklist={checklist}
            projects={projects}
            onToggleItem={(itemId) => handleToggleItem(checklist, itemId)}
            onViewDetail={() => setShowDetailModal(checklist)}
            onDelete={() => handleDeleteChecklist(checklist.checklist_id, false)}
            isManager={canEditDelete}
          />
        ))}
      </div>

      {checklists.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No active checklists</p>
            {isManager && (
              <Button 
                variant="outline" 
                className="mt-4" 
                onClick={() => setShowCreateModal(true)}
              >
                Create First Checklist
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Checklist Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Checklist</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateChecklist} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newChecklist.title}
                onChange={(e) => setNewChecklist({ ...newChecklist, title: e.target.value })}
                placeholder="e.g., Kitchen Installation Checklist"
                required
                data-testid="checklist-title"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newChecklist.description}
                onChange={(e) => setNewChecklist({ ...newChecklist, description: e.target.value })}
                placeholder="What is this checklist for?"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Project (optional)</Label>
                <Select
                  value={newChecklist.project_id || "none"}
                  onValueChange={(v) => setNewChecklist({ ...newChecklist, project_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-6">
                <Checkbox
                  id="is_template"
                  checked={newChecklist.is_template}
                  onCheckedChange={(checked) => setNewChecklist({ ...newChecklist, is_template: checked })}
                />
                <Label htmlFor="is_template" className="cursor-pointer">Save as template</Label>
              </div>
            </div>

            {/* Visibility */}
            <div>
              <Label>Who can see this list?</Label>
              <Select
                value={newChecklist.visibility}
                onValueChange={(v) => setNewChecklist({ ...newChecklist, visibility: v })}
              >
                <SelectTrigger data-testid="checklist-visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone (all team members)</SelectItem>
                  <SelectItem value="owner_only">Owner Only</SelectItem>
                  <SelectItem value="office_only">Office Only (Owner + PM + Office Admin)</SelectItem>
                  <SelectItem value="project_team">Project Team Only (assigned members)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Add Items Section */}
            <div>
              <Label>Checklist Items</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newItemTitle}
                  onChange={(e) => setNewItemTitle(e.target.value)}
                  placeholder="Item title"
                  className="flex-1"
                  data-testid="item-title-input"
                />
                <Input
                  value={newItemEstimate}
                  onChange={(e) => setNewItemEstimate(e.target.value)}
                  placeholder="Est. min"
                  className="w-24"
                  type="number"
                />
                <Button type="button" variant="outline" onClick={addItemToForm}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {newChecklist.items.length > 0 && (
                <div className="mt-3 space-y-2">
                  {newChecklist.items.map((item, index) => (
                    <div key={`task-${index}-${item.title || index}`} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                      <CheckSquare className="w-4 h-4 text-slate-400" />
                      <span className="flex-1 text-sm">{item.title}</span>
                      {item.time_estimate_minutes && (
                        <span className="text-xs text-slate-500">{item.time_estimate_minutes}m</span>
                      )}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => removeItemFromForm(index)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newChecklist.title} data-testid="save-checklist-btn">
                Create Checklist
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Checklist Detail Modal */}
      <Dialog open={!!showDetailModal} onOpenChange={() => setShowDetailModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showDetailModal?.title}
              {showDetailModal?.completion && (
                <Badge className={
                  showDetailModal?.completion?.percentage === 100 
                    ? "bg-green-100 text-green-700" 
                    : "bg-slate-100 text-slate-700"
                }>
                  {showDetailModal?.completion?.completed}/{showDetailModal?.completion?.total}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {showDetailModal && (
            <div className="space-y-4">
              {showDetailModal.completion && (
                <Progress value={showDetailModal?.completion?.percentage} className="h-2" />
              )}

              {showDetailModal.description && (
                <p className="text-sm text-slate-600">{showDetailModal.description}</p>
              )}

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(showDetailModal.items || []).map(item => (
                  <div 
                    key={item.item_id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      item.is_completed ? 'bg-green-50 border-green-200' : 'border-slate-200'
                    }`}
                  >
                    <Checkbox
                      checked={item.is_completed}
                      onCheckedChange={() => handleToggleItem(showDetailModal, item.item_id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${item.is_completed ? 'line-through text-slate-400' : ''}`}>
                        {item.title}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        {item.time_estimate_minutes && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.time_estimate_minutes}m
                          </span>
                        )}
                        {item.completed_by_user && (
                          <span className="text-xs text-slate-500">
                            by {item.completed_by_user.name}
                          </span>
                        )}
                      </div>
                    </div>
                    {canEditDelete && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => handleDeleteItem(showDetailModal.checklist_id, item.item_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Item Inline */}
              <div className="flex gap-2 pt-2 border-t">
                <Input
                  value={newItemTitle}
                  onChange={(e) => setNewItemTitle(e.target.value)}
                  placeholder="Add new item..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddItem(showDetailModal.checklist_id);
                    }
                  }}
                />
                <Button 
                  variant="outline"
                  onClick={() => handleAddItem(showDetailModal.checklist_id)}
                  disabled={!newItemTitle.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Checklist Card Component
function ChecklistCard({ checklist, projects, onToggleItem, onViewDetail, onDelete, isManager }) {
  const [expanded, setExpanded] = useState(false);
  const project = projects.find(p => p.project_id === checklist.project_id);
  const completion = checklist.completion || { total: 0, completed: 0, percentage: 0 };

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={onViewDetail}
      data-testid={`checklist-card-${checklist.checklist_id}`}
    >
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-slate-900 truncate">{checklist.title}</h3>
            <div className="flex items-center gap-1 mt-0.5">
              {project && (
                <span className="text-sm text-slate-500 truncate">{project.name}</span>
              )}
              {checklist.visibility && checklist.visibility !== "everyone" && (
                <Badge variant="outline" className={`text-[10px] px-1 ${checklist.visibility === "owner_only" ? "text-red-600 border-red-200" : checklist.visibility === "office_only" ? "text-purple-600 border-purple-200" : "text-blue-600 border-blue-200"}`}>
                  {checklist.visibility === "owner_only" ? "Owner" : checklist.visibility === "office_only" ? "Office" : "Project Team"}
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onViewDetail}>
                <CheckSquare className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              {isManager && (
                <DropdownMenuItem onClick={onDelete} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
            <span>{completion.completed} of {completion.total} completed</span>
            <span>{completion.percentage}%</span>
          </div>
          <Progress value={completion.percentage} className="h-2" />
        </div>

        {/* Quick Items Preview */}
        <div className="mt-3">
          <button 
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {checklist.items.slice(0, 3).length} items
          </button>
          
          {expanded && (
            <div className="mt-2 space-y-1">
              {checklist.items.slice(0, 5).map(item => (
                <div 
                  key={item.item_id}
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    checked={item.is_completed}
                    onCheckedChange={() => onToggleItem(item.item_id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className={item.is_completed ? 'line-through text-slate-400' : ''}>
                    {item.title}
                  </span>
                </div>
              ))}
              {checklist.items.length > 5 && (
                <p className="text-xs text-slate-400 pl-6">
                  +{checklist.items.length - 5} more
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
